import React, { useState, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    X, Image as ImageIcon, Video, Loader2, Upload,
    FileText, BarChart3, Plus, EyeOff, Eye, GripVertical, Lock, Globe, Type,
    Presentation
} from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { Modal } from '../common';

const modules = {
    toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'color': [] }, { 'background': [] }],
        ['link'],
        ['clean']
    ],
};

const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet',
    'color', 'background',
    'link'
];

const MAX_VIDEO_SIZE_MB = 300;
const MAX_DECK_SIZE_MB = 45;
const DECK_EXTENSIONS = ['pptx', 'ppt', 'odp'];
// La conversion a diapositivas corre en el servidor y tarda mas que una subida normal
const DECK_UPLOAD_TIMEOUT_MS = 150000;

const emptyPregunta = () => ({ texto: '', tipo: 'opcion_unica', opciones: ['', ''] });

const TITULO_MAX = 200;

const formatFileSize = (bytes) => {
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const DeckPreview = ({ file, onRemove }) => (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50/60 animate-fadeIn">
        <div className="w-11 h-11 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
            <Presentation size={20} />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
            <p className="text-xs text-gray-500 mt-0.5">
                {formatFileSize(file.size)} &middot; Se convierte en diapositivas al publicar
            </p>
        </div>
        <button
            type="button"
            onClick={onRemove}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-white rounded-lg transition-colors flex-shrink-0"
            title="Quitar presentacion"
        >
            <X size={16} />
        </button>
    </div>
);

const MediaPreview = ({ src, kind, onRemove }) => {
    const [orientation, setOrientation] = useState('landscape');
    const isVertical = orientation === 'portrait';
    const maxH = isVertical ? 'max-h-[520px]' : 'max-h-[360px]';

    return (
        <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-900/95 group animate-fadeIn flex items-center justify-center">
            {kind === 'image' ? (
                <img
                    src={src}
                    alt="Preview"
                    className={`w-auto h-auto ${maxH} max-w-full object-contain`}
                    onLoad={(e) => {
                        const { naturalWidth, naturalHeight } = e.target;
                        setOrientation(naturalHeight > naturalWidth * 1.05 ? 'portrait' : 'landscape');
                    }}
                />
            ) : (
                <video
                    src={src}
                    controls
                    preload="metadata"
                    className={`w-auto h-auto ${maxH} max-w-full bg-black`}
                    onLoadedMetadata={(e) => {
                        const { videoWidth, videoHeight } = e.target;
                        if (videoWidth && videoHeight) {
                            setOrientation(videoHeight > videoWidth * 1.05 ? 'portrait' : 'landscape');
                        }
                    }}
                />
            )}
            <button
                type="button"
                onClick={onRemove}
                className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur-sm rounded-full shadow-md text-gray-600 hover:text-red-500 transition-all"
                title="Quitar"
            >
                <X size={16} />
            </button>
            {isVertical && (
                <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-white/90 backdrop-blur-sm text-[10px] font-semibold text-gray-600">
                    Vertical
                </span>
            )}
        </div>
    );
};

const CreatePostModal = ({ isOpen, onClose, onSuccess }) => {
    const [titulo, setTitulo] = useState('');
    const [content, setContent] = useState('');
    const [type, setType] = useState('aviso');
    const [preguntas, setPreguntas] = useState([emptyPregunta()]);
    const [pollDuration, setPollDuration] = useState('24');
    const [isAnonima, setIsAnonima] = useState(false);
    const [resultadosPublicos, setResultadosPublicos] = useState(true);
    const [image, setImage] = useState(null);
    const [video, setVideo] = useState(null);
    const [deck, setDeck] = useState(null);
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [uploadError, setUploadError] = useState(null);

    const imageInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const deckInputRef = useRef(null);
    const dropZoneRef = useRef(null);

    const resetForm = () => {
        setTitulo('');
        setContent('');
        setType('aviso');
        setPreguntas([emptyPregunta()]);
        setPollDuration('24');
        setIsAnonima(false);
        setResultadosPublicos(true);
        setImage(null);
        setVideo(null);
        setDeck(null);
        setImagePreview(null);
        setVideoPreview(null);
        setUploadError(null);
    };

    // --- Preguntas handlers ---
    const updatePreguntaTexto = (pIdx, value) => {
        setPreguntas(prev => prev.map((p, i) => i === pIdx ? { ...p, texto: value } : p));
    };

    const updatePreguntaTipo = (pIdx, tipo) => {
        setPreguntas(prev => prev.map((p, i) => i === pIdx ? { ...p, tipo } : p));
    };

    const updateOpcion = (pIdx, oIdx, value) => {
        setPreguntas(prev => prev.map((p, i) => {
            if (i !== pIdx) return p;
            const ops = [...p.opciones];
            ops[oIdx] = value;
            return { ...p, opciones: ops };
        }));
    };

    const addOpcion = (pIdx) => {
        setPreguntas(prev => prev.map((p, i) =>
            i === pIdx ? { ...p, opciones: [...p.opciones, ''] } : p
        ));
    };

    const removeOpcion = (pIdx, oIdx) => {
        setPreguntas(prev => prev.map((p, i) => {
            if (i !== pIdx || p.opciones.length <= 2) return p;
            return { ...p, opciones: p.opciones.filter((_, j) => j !== oIdx) };
        }));
    };

    const addPregunta = () => {
        if (preguntas.length < 20) setPreguntas(prev => [...prev, emptyPregunta()]);
    };

    const removePregunta = (pIdx) => {
        if (preguntas.length > 1) setPreguntas(prev => prev.filter((_, i) => i !== pIdx));
    };

    // --- Media handlers ---
    const handleImageSelect = (file) => {
        if (file && file.type.startsWith('image/')) {
            setImage(file);
            setImagePreview(URL.createObjectURL(file));
            setUploadError(null);
        }
    };

    const handleVideoSelect = (file) => {
        if (file && file.type.startsWith('video/')) {
            const sizeMB = file.size / (1024 * 1024);
            if (sizeMB > MAX_VIDEO_SIZE_MB) {
                setUploadError(`El video excede el limite de ${MAX_VIDEO_SIZE_MB}MB (${sizeMB.toFixed(1)}MB)`);
                return;
            }
            setVideo(file);
            setVideoPreview(URL.createObjectURL(file));
            setUploadError(null);
        }
    };

    const isDeckFile = (file) => DECK_EXTENSIONS.includes(file.name.split('.').pop()?.toLowerCase());

    const handleDeckSelect = (file) => {
        if (!file) return;
        if (!isDeckFile(file)) {
            setUploadError('Formato no soportado. Usa PPTX, PPT u ODP.');
            return;
        }
        const sizeMB = file.size / (1024 * 1024);
        if (sizeMB > MAX_DECK_SIZE_MB) {
            setUploadError(`La presentacion excede el limite de ${MAX_DECK_SIZE_MB}MB (${sizeMB.toFixed(1)}MB)`);
            return;
        }
        setDeck(file);
        setUploadError(null);
    };

    const handleFileSelect = (file) => {
        if (!file) return;
        if (file.type.startsWith('image/')) handleImageSelect(file);
        else if (file.type.startsWith('video/')) handleVideoSelect(file);
        else if (isDeckFile(file)) handleDeckSelect(file);
        else setUploadError('Formato no soportado. Usa imagenes (PNG, JPG, GIF), videos (MP4, WebM, MOV) o presentaciones (PPTX).');
    };

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
        else if (e.type === 'dragleave') setDragActive(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
    }, []);

    const removeImage = () => {
        setImage(null);
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImagePreview(null);
    };

    const removeDeck = () => {
        setDeck(null);
        if (deckInputRef.current) deckInputRef.current.value = '';
    };

    const removeVideo = () => {
        setVideo(null);
        if (videoPreview) URL.revokeObjectURL(videoPreview);
        setVideoPreview(null);
    };

    // --- Submit ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!content.trim() || content === '<p><br></p>') return;

        if (type === 'encuesta') {
            for (let i = 0; i < preguntas.length; i++) {
                if (!preguntas[i].texto.trim()) {
                    setUploadError(`La pregunta ${i + 1} no tiene texto`);
                    return;
                }
                if ((preguntas[i].tipo || 'opcion_unica') !== 'texto') {
                    const validOps = preguntas[i].opciones.filter(o => o.trim());
                    if (validOps.length < 2) {
                        setUploadError(`La pregunta ${i + 1} necesita al menos 2 opciones`);
                        return;
                    }
                }
            }
        }

        setLoading(true);
        setUploadError(null);

        const formData = new FormData();
        if (titulo.trim()) formData.append('titulo', titulo.trim());
        formData.append('content', content);
        formData.append('type', type);

        if (type === 'encuesta') {
            const preguntasPayload = preguntas.map(p => ({
                texto: p.texto.trim(),
                tipo: p.tipo || 'opcion_unica',
                opciones: p.tipo === 'texto' ? [] : p.opciones.filter(o => o.trim())
            }));
            formData.append('preguntas', JSON.stringify(preguntasPayload));
            formData.append('expiration_hours', pollDuration);
            formData.append('is_anonima', isAnonima ? 'true' : 'false');
            formData.append('resultados_publicos', resultadosPublicos ? 'true' : 'false');
        }

        if (image) formData.append('image', image);
        if (video) formData.append('video', video);
        if (deck) formData.append('presentacion', deck);

        try {
            await axiosInstance.post('/api/publicaciones', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                ...(deck ? { timeout: DECK_UPLOAD_TIMEOUT_MS } : {}),
            });
            resetForm();
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error creating post:', error);
            setUploadError(error.response?.data?.error || 'Error al crear la publicacion');
        } finally {
            setLoading(false);
        }
    };

    const hasContent = content.trim() && content !== '<p><br></p>';
    const hasMedia = image || video || deck;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="3xl">
            <Modal.Header onClose={onClose}>
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl text-white shadow-lg shadow-blue-200">
                        <FileText size={20} />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">Nueva Publicacion</h3>
                        <p className="text-xs text-gray-500 font-normal">Comparte con todo el equipo</p>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body>
                <form onSubmit={handleSubmit} id="create-post-form" className="space-y-5">

                    {/* Type Selector */}
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => setType('aviso')}
                            className={`flex-1 flex items-center justify-center gap-2.5 py-3 px-4 rounded-xl border-2 transition-all duration-200 ${
                                type === 'aviso'
                                    ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm shadow-blue-100'
                                    : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            <FileText size={18} />
                            <span className="font-semibold text-sm">Aviso General</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setType('encuesta')}
                            className={`flex-1 flex items-center justify-center gap-2.5 py-3 px-4 rounded-xl border-2 transition-all duration-200 ${
                                type === 'encuesta'
                                    ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-sm shadow-purple-100'
                                    : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            <BarChart3 size={18} />
                            <span className="font-semibold text-sm">Encuesta</span>
                        </button>
                    </div>

                    {/* Titulo */}
                    <div className="relative rounded-xl border border-blue-100 bg-white shadow-sm focus-within:border-blue-400 focus-within:shadow-md focus-within:shadow-blue-100/60 transition-all overflow-hidden">
                        <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-blue-400 via-indigo-400 to-blue-400" />
                        <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                            <Type size={14} className="text-blue-500" />
                            <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600">Título</span>
                            <span className="ml-auto text-[10px] text-gray-400 tabular-nums">{titulo.length}/{TITULO_MAX}</span>
                        </div>
                        <input
                            type="text"
                            value={titulo}
                            onChange={(e) => setTitulo(e.target.value.slice(0, TITULO_MAX))}
                            placeholder="Título"
                            className="w-full bg-transparent border-0 px-4 pb-3 pt-0 text-lg font-bold text-gray-900 placeholder:text-gray-400 placeholder:font-medium focus:outline-none"
                            maxLength={TITULO_MAX}
                        />
                    </div>

                    {/* Rich Text Editor */}
                    <div className="relative">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5 ml-1">Contenido</div>
                        <ReactQuill
                            key={type}
                            theme="snow"
                            value={content}
                            onChange={setContent}
                            modules={modules}
                            formats={formats}
                            className="rounded-xl overflow-hidden [&_.ql-toolbar]:border-gray-200 [&_.ql-toolbar]:bg-gray-50/80 [&_.ql-toolbar]:rounded-t-xl [&_.ql-container]:border-gray-200 [&_.ql-container]:rounded-b-xl [&_.ql-editor]:min-h-[120px] [&_.ql-editor]:max-h-[200px] [&_.ql-editor]:text-gray-700"
                            placeholder={type === 'encuesta' ? "Contexto o descripción de la encuesta..." : "¿Qué quieres compartir con el equipo?"}
                        />
                    </div>

                    {/* Poll Section */}
                    {type === 'encuesta' && (
                        <div className="animate-fadeIn space-y-3">
                            <div className="p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-100 space-y-4">

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <BarChart3 size={16} className="text-purple-600" />
                                        <span className="text-sm font-bold text-purple-800">Preguntas</span>
                                        <span className="text-xs text-purple-500 bg-purple-100 px-2 py-0.5 rounded-full">{preguntas.length}/20</span>
                                    </div>
                                </div>

                                {/* Preguntas */}
                                <div className="space-y-4">
                                    {preguntas.map((pregunta, pIdx) => (
                                        <div key={pIdx} className="bg-white rounded-xl border border-purple-100 p-3 space-y-2.5 shadow-sm">
                                            {/* Pregunta header */}
                                            <div className="flex items-center gap-2">
                                                <GripVertical size={14} className="text-gray-300 flex-shrink-0" />
                                                <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-md">
                                                    Pregunta {pIdx + 1}
                                                </span>
                                                <div className="flex rounded-lg border border-purple-200 overflow-hidden ml-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => updatePreguntaTipo(pIdx, 'opcion_unica')}
                                                        className={`px-2 py-1 text-[11px] font-semibold transition-colors ${(pregunta.tipo || 'opcion_unica') !== 'texto' ? 'bg-purple-600 text-white' : 'bg-white text-purple-500 hover:bg-purple-50'}`}
                                                    >
                                                        Opciones
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => updatePreguntaTipo(pIdx, 'texto')}
                                                        className={`px-2 py-1 text-[11px] font-semibold transition-colors ${pregunta.tipo === 'texto' ? 'bg-purple-600 text-white' : 'bg-white text-purple-500 hover:bg-purple-50'}`}
                                                    >
                                                        Respuesta abierta
                                                    </button>
                                                </div>
                                                {preguntas.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removePregunta(pIdx)}
                                                        className="ml-auto p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                )}
                                            </div>

                                            <input
                                                type="text"
                                                value={pregunta.texto}
                                                onChange={(e) => updatePreguntaTexto(pIdx, e.target.value)}
                                                placeholder="Escribe la pregunta..."
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 placeholder:text-gray-400"
                                            />

                                            {/* Opciones (solo tipo opcion_unica) */}
                                            {pregunta.tipo === 'texto' ? (
                                                <div className="pl-1">
                                                    <div className="px-3 py-2.5 bg-gray-50 border border-dashed border-gray-300 rounded-lg text-sm text-gray-400 italic">
                                                        Respuesta abierta: cada quien escribe su respuesta
                                                    </div>
                                                </div>
                                            ) : (
                                            <div className="space-y-1.5 pl-1">
                                                {pregunta.opciones.map((opt, oIdx) => (
                                                    <div key={oIdx} className="flex gap-2 items-center">
                                                        <div className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                                            {oIdx + 1}
                                                        </div>
                                                        <input
                                                            type="text"
                                                            value={opt}
                                                            onChange={(e) => updateOpcion(pIdx, oIdx, e.target.value)}
                                                            placeholder={`Opcion ${oIdx + 1}`}
                                                            className="flex-1 px-3 py-2 bg-white border border-purple-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 placeholder:text-gray-400"
                                                        />
                                                        {pregunta.opciones.length > 2 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeOpcion(pIdx, oIdx)}
                                                                className="p-1 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                                                            >
                                                                <X size={13} />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={() => addOpcion(pIdx)}
                                                    className="text-xs text-purple-500 hover:text-purple-700 flex items-center gap-1 px-1 py-0.5 hover:bg-purple-50 rounded transition-colors ml-7"
                                                >
                                                    <Plus size={12} /> Agregar opcion
                                                </button>
                                            </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {preguntas.length < 20 && (
                                    <button
                                        type="button"
                                        onClick={addPregunta}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-purple-200 rounded-xl text-sm text-purple-500 hover:text-purple-700 hover:border-purple-400 hover:bg-purple-50/50 transition-all"
                                    >
                                        <Plus size={16} />
                                        Agregar otra pregunta
                                    </button>
                                )}

                                {/* Duracion + Configuracion de privacidad */}
                                <div className="pt-3 border-t border-purple-100 space-y-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-purple-700 mb-1.5">Duración</label>
                                        <select
                                            value={pollDuration}
                                            onChange={(e) => setPollDuration(e.target.value)}
                                            className="w-full px-3 py-2 bg-white border border-purple-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                                        >
                                            <option value="24">24 horas</option>
                                            <option value="48">2 días</option>
                                            <option value="72">3 días</option>
                                            <option value="168">1 semana</option>
                                        </select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-semibold text-purple-700 mb-1.5">Votantes</label>
                                            <button
                                                type="button"
                                                onClick={() => setIsAnonima(!isAnonima)}
                                                className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                                                    isAnonima
                                                        ? 'bg-gray-800 border-gray-800 text-white'
                                                        : 'bg-white border-purple-200 text-purple-700 hover:bg-purple-50'
                                                }`}
                                            >
                                                {isAnonima ? <EyeOff size={15} /> : <Eye size={15} />}
                                                {isAnonima ? 'Anónimos' : 'Visibles'}
                                            </button>
                                            <p className="text-[10px] text-gray-400 mt-1 text-center">
                                                {isAnonima ? 'Nadie ve quién votó' : 'Creador ve quién votó'}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-purple-700 mb-1.5">Resultados</label>
                                            <button
                                                type="button"
                                                onClick={() => setResultadosPublicos(!resultadosPublicos)}
                                                className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                                                    !resultadosPublicos
                                                        ? 'bg-gray-800 border-gray-800 text-white'
                                                        : 'bg-white border-purple-200 text-purple-700 hover:bg-purple-50'
                                                }`}
                                            >
                                                {resultadosPublicos ? <Globe size={15} /> : <Lock size={15} />}
                                                {resultadosPublicos ? 'Públicos' : 'Privados'}
                                            </button>
                                            <p className="text-[10px] text-gray-400 mt-1 text-center">
                                                {resultadosPublicos ? 'Todos ven cómo va' : 'Solo el creador ve'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Media Upload Section */}
                    <div className="space-y-3">
                        {!hasMedia && (
                            <div
                                ref={dropZoneRef}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                className={`relative border-2 border-dashed rounded-xl p-6 transition-all duration-200 ${
                                    dragActive
                                        ? 'border-blue-400 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300 bg-gray-50/50'
                                }`}
                            >
                                <div className="flex flex-col items-center gap-3">
                                    <div className={`p-3 rounded-full transition-colors ${dragActive ? 'bg-blue-100' : 'bg-gray-100'}`}>
                                        <Upload size={22} className={dragActive ? 'text-blue-500' : 'text-gray-400'} />
                                    </div>
                                    <div className="text-center">
                                        <p className="text-sm font-medium text-gray-600">Arrastra un archivo aqui o</p>
                                        <div className="flex items-center justify-center gap-3 mt-2">
                                            <button
                                                type="button"
                                                onClick={() => imageInputRef.current?.click()}
                                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
                                            >
                                                <ImageIcon size={16} className="text-green-500" />
                                                Imagen
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => videoInputRef.current?.click()}
                                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
                                            >
                                                <Video size={16} className="text-blue-500" />
                                                Video
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => deckInputRef.current?.click()}
                                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
                                            >
                                                <Presentation size={16} className="text-amber-500" />
                                                Presentacion
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-400 mt-2">PNG, JPG, GIF, MP4, WebM, MOV, PPTX (max {MAX_VIDEO_SIZE_MB}MB para videos, {MAX_DECK_SIZE_MB}MB para presentaciones)</p>
                                    </div>
                                </div>
                                <input ref={imageInputRef} type="file" accept="image/png,image/jpg,image/jpeg,image/gif" onChange={(e) => handleImageSelect(e.target.files[0])} className="hidden" />
                                <input ref={videoInputRef} type="file" accept="video/mp4,video/webm,video/quicktime" onChange={(e) => handleVideoSelect(e.target.files[0])} className="hidden" />
                                <input ref={deckInputRef} type="file" accept=".pptx,.ppt,.odp" onChange={(e) => handleDeckSelect(e.target.files[0])} className="hidden" />
                            </div>
                        )}

                        {deck && (
                            <DeckPreview file={deck} onRemove={removeDeck} />
                        )}

                        {imagePreview && (
                            <MediaPreview src={imagePreview} kind="image" onRemove={removeImage} />
                        )}

                        {videoPreview && (
                            <MediaPreview src={videoPreview} kind="video" onRemove={removeVideo} />
                        )}

                        {(image && !video) && (
                            <>
                                <button type="button" onClick={() => videoInputRef.current?.click()} className="flex items-center gap-2 text-sm text-gray-500 hover:text-blue-600 px-3 py-2 hover:bg-blue-50 rounded-lg transition-colors">
                                    <Video size={16} /><span>Agregar video tambien</span>
                                </button>
                                <input ref={videoInputRef} type="file" accept="video/mp4,video/webm,video/quicktime" onChange={(e) => handleVideoSelect(e.target.files[0])} className="hidden" />
                            </>
                        )}
                        {(video && !image) && (
                            <>
                                <button type="button" onClick={() => imageInputRef.current?.click()} className="flex items-center gap-2 text-sm text-gray-500 hover:text-green-600 px-3 py-2 hover:bg-green-50 rounded-lg transition-colors">
                                    <ImageIcon size={16} /><span>Agregar imagen tambien</span>
                                </button>
                                <input ref={imageInputRef} type="file" accept="image/png,image/jpg,image/jpeg,image/gif" onChange={(e) => handleImageSelect(e.target.files[0])} className="hidden" />
                            </>
                        )}
                    </div>

                    {uploadError && (
                        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm animate-fadeIn">
                            <X size={16} className="flex-shrink-0" />
                            <span>{uploadError}</span>
                        </div>
                    )}
                </form>
            </Modal.Body>

            <Modal.Footer>
                <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                        {hasMedia && (
                            <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md">
                                {image && <><ImageIcon size={12} className="text-green-500" /> Imagen</>}
                                {image && video && <span className="mx-1">+</span>}
                                {video && <><Video size={12} className="text-blue-500" /> Video</>}
                                {deck && <><Presentation size={12} className="text-amber-500" /> Presentacion</>}
                            </span>
                        )}
                        {type === 'encuesta' && isAnonima && (
                            <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md">
                                <EyeOff size={12} /> Anónima
                            </span>
                        )}
                        {type === 'encuesta' && !resultadosPublicos && (
                            <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md">
                                <Lock size={12} /> Privada
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => { resetForm(); onClose(); }}
                            className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            form="create-post-form"
                            disabled={loading || !hasContent}
                            className={`px-6 py-2.5 font-semibold rounded-xl transition-all flex items-center gap-2 shadow-sm ${
                                type === 'encuesta'
                                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-purple-200 disabled:from-purple-300 disabled:to-indigo-300'
                                    : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-blue-200 disabled:from-blue-300 disabled:to-blue-400'
                            } disabled:cursor-not-allowed disabled:shadow-none`}
                        >
                            {loading && <Loader2 size={16} className="animate-spin" />}
                            {loading ? (deck ? 'Convirtiendo diapositivas...' : 'Publicando...') : 'Publicar'}
                        </button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default CreatePostModal;
